<script setup>
import { WdsIcon } from '@wds/components';
import { computed, onMounted, ref } from 'vue';
import { useMq } from '@/composables/useMq';

const props = defineProps({
  type: {
    type: String,
    default: 'neutral',
    validator: (t) => ['info', 'success', 'warning', 'error', 'neutral'].includes(t),
  },
  message: {
    type: String,
    required: false,
    default: null,
  },
  icon: {
    type: String,
    required: false,
    default: null,
  },
  iconColor: {
    type: String,
    required: false,
    default: null,
    validator: (t) => ['blue', 'yellow'].includes(t),
  },
  timeout: {
    type: Number,
    required: false,
    default: 3000,
  },
  position: {
    type: String,
    default: 'top-right',
    validator: (t) => ['top-right', 'bottom-right'].includes(t),
  },
  html: {
    type: String,
    required: false,
    default: null,
  },
});

const emit = defineEmits(['dismiss']);

const { isBelowMediumScreen } = useMq();
const isVisible = ref(true);

const derivedIcon = computed(() => {
  if (props.icon) {
    return props.icon;
  }
  switch (props.type) {
    case 'success':
      return 'success';
    case 'warning':
      return 'alert-circle';
    case 'error':
      return 'info';
    default:
      return 'info';
  }
});

const derivedPosition = computed(() => {
  return isBelowMediumScreen.value ? 'bottom-center' : props.position;
});

const transition = computed(() => {
  switch (derivedPosition.value) {
    case 'top-right':
    case 'bottom-right':
      return 'slide-left';
    case 'bottom-center':
      return 'slide-up';
    default:
      return 'slide-left';
  }
});

function dismiss() {
  isVisible.value = false;
  emit('dismiss');
}

onMounted(() => {
  setTimeout(() => {
    dismiss();
  }, props.timeout);
});
</script>

<template>
  <Transition :name="transition">
    <div
      v-if="isVisible"
      class="toast body-2"
      :class="[`type-${type}`, `position-${derivedPosition}`]"
      @click="dismiss"
    >
      <span
        class="dismiss"
        :class="[`dismiss-type-${type}`]"
      >
        <WdsIcon asset="close" />
      </span>
      <div class="content">
        <span
          class="dismiss"
          :class="[`dismiss-type-${type}`]"
        >
          <WdsIcon asset="close" />
        </span>
        <span
          class="icon"
          :class="[iconColor && `icon-color-${iconColor}`]"
        >
          <WdsIcon :asset="derivedIcon" />
        </span>
        <p class="message">
          <!-- eslint-disable-next-line vue/no-v-html -->
          <span
            v-if="html"
            v-html="html"
          />
          <template v-else>
            {{ message }}
          </template>
        </p>
      </div>
    </div>
  </Transition>
</template>

<style lang="scss" scoped>
.toast {
  position: fixed;
  padding: $wds-spacing-m;
  width: 75vw;
  max-width: wds-rem(328px);
  z-index: 550;
  box-shadow: $wds-shadow-light-s;
  border-radius: $wds-border-radius-s;
  cursor: pointer;

  @include tablet {
    width: wds-rem(328px);
  }

  @include desktop {
    width: wds-rem(328px);
  }
}

.type-neutral {
  background-color: $wds-color-white;
  color: $wds-color-ink-dark;
}

.type-info {
  background-color: $wds-color-blue-lightest;
  color: $wds-color-blue;
}

.type-success {
  background-color: $wds-color-green-lightest;
  color: $wds-color-green;
}

.type-warning {
  background-color: $wds-color-yellow-lightest;
  color: $wds-color-yellow;
}

.type-error {
  background-color: $wds-color-red-lightest;
  color: $wds-color-red;
}

.position-top-right {
  top: $wds-spacing-l;
  right: $wds-spacing-l;
}

.position-bottom-right {
  bottom: $wds-spacing-l;
  right: $wds-spacing-l;
}

.position-bottom-center {
  bottom: $wds-spacing-l;
  left: 50%;
  transform: translate(-50%);
}

.dismiss {
  display: flex;
  align-items: center;
  position: absolute;
  top: $wds-spacing-s;
  right: $wds-spacing-s;
  font-size: $wds-font-size-body-3;

  &-type-neutral {
    color: $wds-color-ink-light;
  }

  &-type-info {
    color: $wds-color-blue-light;
  }

  &-type-success {
    color: $wds-color-green-light;
  }

  &-type-warning {
    color: $wds-color-yellow-light;
  }

  &-type-error {
    color: $wds-color-red-light;
  }
}

.content {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-right: $wds-spacing-m;
}

.icon {
  display: flex;
  align-items: center;
  margin-right: $wds-spacing-s;
  font-size: $wds-font-size-body-1;

  &-color-blue {
    color: $wds-color-blue;
  }

  &-color-yellow {
    color: $wds-color-yellow;
  }
}

.message {
  overflow-wrap: anywhere;
}

// Transitions
.slide-left-enter-active,
.slide-left-leave-active {
  transition: all 0.3s ease;
}

.slide-left-enter-from {
  opacity: 0;
  transform: translateX(100%);
}

.slide-left-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s ease;
}

.slide-up-enter-from {
  opacity: 0;
  transform: translate(-50%, 100%);
}

.slide-up-leave-to {
  opacity: 0;
  transform: translate(-50%, 100%);
}
</style>
